import { useCallback } from 'react';

import { DEFAULT_CURRENCY } from '@/data/config/DEFAULTS';
import { usePersonInfo } from '@/data/Content/PersonInfo';
import { useSettings } from '@/data/Settings';
import { getExternalId } from '@/data/utils/externalId';
import { dFix } from '@/data/utils/floatingPoint';
import { TiktokClientPayload } from '@/pages/api/tracking/tiktok/types';
import { useCurrentPhysicalStoreId } from 'presentation/events/hooks/useCurrentPhysicalStoreId';
import { useTrackingStoreLocation } from 'presentation/events/hooks/useTrackingStoreLocation';
import { useTikTokClickIdentifiers } from 'presentation/events/hooks/useClickIdentifiers';
import { triggerTikTokConversionEvent } from 'presentation/events/services/tiktokService';
import { getReferrerForTracking, safeDecodeUrl } from 'presentation/events/utils/ga4Utils';
import {
	getItemPartNumber,
	getProductPrice,
	getProductQuantity,
} from 'presentation/events/utils/productUtils';
import { SendAddToCartEventProps } from './types';

export const useTrackAddToCartTikTok = () => {
	const { settings } = useSettings();
	const { physicalStoreId } = useCurrentPhysicalStoreId();
	const storeLocation = useTrackingStoreLocation();
	const { personInfo } = usePersonInfo();
	const { ttclid, ttp } = useTikTokClickIdentifiers();

	return useCallback(
		(props: SendAddToCartEventProps) => {
			if (settings.userData?.ENABLE_TIKTOK_CALL !== 'true' || !physicalStoreId) return;

			const { storeId } = props;

			let contents: Array<{
				content_id: string;
				price: number;
				quantity: number;
				content_category: string;
				content_name: string;
				brand: string;
			}> = [];

			if (props.product) {
				// Single product

				const { product, promotionDetails, categories } = props;

				const quantity = getProductQuantity({ quantity: props.quantity });

				const { price } = getProductPrice({
					product,
					quantity,
					physicalStoreId,
					promotionDetails,
				});

				contents = [
					{
						content_id: getItemPartNumber(product),
						price,
						quantity,
						content_category: categories.join(' > '),
						content_name: product.name,
						brand: product.manufacturer,
					},
				];
			}

			if (props.items) {
				// Multiple products

				contents = props.items.map((item) => {
					const { product, promotionDetails, categories } = item;

					const quantity = getProductQuantity({ quantity: item.quantity });

					const { price } = getProductPrice({
						product,
						quantity,
						physicalStoreId,
						promotionDetails,
					});

					return {
						content_id: getItemPartNumber(product),
						price,
						quantity,
						content_category: categories.join(' > '),
						content_name: product.name,
						brand: product.manufacturer,
					};
				});
			}

			const value = dFix(
				contents.reduce((sum, item) => sum + item.price * item.quantity, 0),
				2
			);
			const content_ids = contents.map((c) => c.content_id);
			const quantity = contents.reduce((sum, item) => sum + item.quantity, 0);

			const payload: TiktokClientPayload = {
				event: 'AddToCart',
				event_id: props.eventId,
				properties: {
					content_type: 'product',
					currency: DEFAULT_CURRENCY,
					value,
					quantity,
					num_items: contents.length,
					content_ids,
					contents,
				},
				user: {
					email: [personInfo?.email1 ?? ''],
					first_name: personInfo?.firstName,
					last_name: personInfo?.lastName,
					phone: [personInfo?.phone1 ?? ''],
					city: storeLocation?.city,
					state: storeLocation?.state,
					country: 'mx',
					external_id: [getExternalId()],
					locale: 'es-MX',
					zip_code: storeLocation?.zipCode,
					ttp,
					ttclid,
				},

				page: {
					url: safeDecodeUrl(location.href),
					referrer: getReferrerForTracking(),
				},
				storeId,
				test_event_code: settings.userData?.TIKTOK_TEST_CODE,
			};

			triggerTikTokConversionEvent(payload);
		},
		[
			settings.userData?.ENABLE_TIKTOK_CALL,
			settings.userData?.TIKTOK_TEST_CODE,
			physicalStoreId,
			storeLocation,
			personInfo?.email1,
			personInfo?.firstName,
			personInfo?.lastName,
			personInfo?.phone1,
			ttp,
			ttclid,
		]
	);
};
